import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import TopBar from '../../components/TopBar'
import Modal from '../../components/Modal'
import LegalDocContent, { getLegalDocTitle } from '../../components/LegalDocContent'
import { useAnalytics } from '../../context/AnalyticsContext'

const TERMS_PDF_URL = '/legal/terms-and-conditions.pdf'
import { useAuth } from '../../context/AuthContext'
import { supabase } from '../../lib/supabase'
import { getOnboarding } from './onboardingStorage'
import { saveOnboardingToProfile } from '../../lib/onboarding'
import { sendEmailNotification } from '../../lib/sendEmailNotification'
import { TERMS_VERSION } from '../../content/legalDocuments'
import './onboarding.css'

const OnboardingPasswordNew = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { trackEvent } = useAnalytics()
  const { signUp, signIn, refreshProfile } = useAuth()
  const saved = getOnboarding()

  const [password, setPassword] = useState('')
  const [agree, setAgree] = useState(false)
  const [error, setError] = useState('')
  const [legal, setLegal] = useState(null)
  const [loading, setLoading] = useState(false)

  const onBack = () => navigate('/onboarding/email')

  const onContinue = async () => {
    if (!password || password.trim().length < 6) {
      setError(t('onboardingPassword.passwordMinLength'))
      return
    }
    if (password.length > 50) {
      setError(t('onboardingPassword.passwordMaxLength'))
      return
    }
    if (/\s/.test(password)) {
      setError(t('changePassword.passwordNoSpaces'))
      return
    }
    if (!agree) {
      setError(t('onboardingPassword.agreePoliciesRequired'))
      return
    }
    if (!saved.email) {
      setError(t('loginPassword.emailRequired'))
      return
    }

    setError('')
    setLoading(true)

    try {
      console.log('[OnboardingPasswordNew] Attempting signUp for:', saved.email)

      const { user, error: signUpError } = await signUp(saved.email, password, saved)
      console.log('[OnboardingPasswordNew] signUp result:', { hasUser: !!user, error: signUpError })

      if (signUpError) {
        const errorLower = signUpError.toLowerCase()
        console.error('[OnboardingPasswordNew] SignUp error:', signUpError)

        // Если Supabase говорит, что email уже зарегистрирован — отправляем на экран входа
        if (
          errorLower.includes('already registered') ||
          errorLower.includes('already exists') ||
          errorLower.includes('user already registered') ||
          errorLower.includes('email address is already registered')
        ) {
          setLoading(false)
          navigate('/onboarding/password/existing', { replace: true })
          return
        }

        // Rate limit / invalid email — возвращаем на шаг email с сообщением
        if (errorLower.includes('rate limit') || errorLower.includes('429') || errorLower.includes('too many')) {
          const message = t('onboardingPassword.rateLimit')
          try {
            localStorage.setItem('astromatch_rate_limit_until', String(Date.now() + 60 * 60 * 1000))
          } catch (_) {}
          navigate('/onboarding/email', {
            replace: true,
            state: { emailError: message },
          })
          return
        }

        if (errorLower.includes('invalid') && errorLower.includes('email')) {
          const message = t('onboardingPassword.emailInvalidForSignup')
          navigate('/onboarding/email', {
            replace: true,
            state: { emailError: message },
          })
          return
        }

        // Ошибки базы данных или сервера
        if (errorLower.includes('database') || errorLower.includes('500') || errorLower.includes('server')) {
          setError(t('onboardingPassword.databaseError'))
          setLoading(false)
          return
        }

        // Остальные ошибки показываем здесь
        setError(signUpError)
        setLoading(false)
        return
      }

      if (!user) {
        setError(t('onboardingPassword.userNotCreated'))
        setLoading(false)
        return
      }

      // Успешная регистрация нового пользователя → сохраняем профиль и переходим в App
      console.log('[OnboardingPasswordNew] ✅ SignUp successful, user ID:', user.id)

      try {
        localStorage.removeItem('astromatch_rate_limit_until')
      } catch (_) {}

      try {
        trackEvent('funnel_password_new_completed', { step: 10 })
      } catch (_) {}

      // Проверяем сессию после signUp
      const { data: sessionData } = await supabase.auth.getSession()
      if (!sessionData?.session) {
        console.log('[OnboardingPasswordNew] No session after signUp, attempting signIn...')
        // Пробуем войти сразу после регистрации
        const { user: signInUser, error: signInError } = await signIn(saved.email, password)
        if (signInError || !signInUser) {
          console.error('[OnboardingPasswordNew] Failed to sign in after signUp:', signInError)
          setError(t('onboardingPassword.signInAfterSignUpFailed'))
          setLoading(false)
          return
        }
        console.log('[OnboardingPasswordNew] ✅ Signed in successfully after signUp')
      }

      // Сохраняем данные профиля и факт согласия с Terms/Privacy
      console.log('[OnboardingPasswordNew] Saving profile data...')
      const consentData = {
        ...saved,
        termsAcceptedAt: new Date().toISOString(),
        termsVersion: TERMS_VERSION
      }
      const { error: saveError } = await saveOnboardingToProfile(user.id, consentData)
      
      if (saveError) {
        console.error('[OnboardingPasswordNew] Error saving profile:', saveError)
        setError(t('onboardingPassword.profileSaveError'))
        setLoading(false)
        return
      }

      console.log('[OnboardingPasswordNew] ✅ Profile saved successfully')
      
      // Обновляем профиль в AuthContext
      console.log('[OnboardingPasswordNew] Refreshing profile in AuthContext...')
      if (refreshProfile) {
        await refreshProfile()
      }
      
      // Ждем и проверяем, что профиль действительно загрузился с данными
      console.log('[OnboardingPasswordNew] Waiting for profile to load...')
      let profileLoaded = false
      for (let i = 0; i < 10; i++) {
        await new Promise(resolve => setTimeout(resolve, 300))
        
        // Проверяем профиль напрямую в БД
        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .select('name, gender, looking_for, date_of_birth, photos')
          .eq('id', user.id)
          .single()
        
        if (!profileError && profileData) {
          if (profileData.name && profileData.gender && profileData.looking_for && profileData.date_of_birth) {
            console.log('[OnboardingPasswordNew] ✅ Profile confirmed loaded with all required data')
            profileLoaded = true
            break
          } else {
            console.log('[OnboardingPasswordNew] Profile loaded but missing data:', {
              hasName: !!profileData.name,
              hasGender: !!profileData.gender,
              hasLookingFor: !!profileData.looking_for,
              hasDateOfBirth: !!profileData.date_of_birth
            })
          }
        } else {
          console.log('[OnboardingPasswordNew] Profile not loaded yet, retrying...', i + 1)
        }
      }
      
      if (!profileLoaded) {
        console.warn('[OnboardingPasswordNew] ⚠️ Profile may not be fully loaded, but proceeding anyway')
      }
      
      setLoading(false)

      try {
        trackEvent('funnel_onboarding_completed', { step: 10 })
      } catch (_) {}

      // Письмо пользователю «успешная регистрация» (Resend → webhook → Telegram тебе)
      try {
        await sendEmailNotification('registration_success', user.id)
      } catch (e) {
        console.warn('[OnboardingPasswordNew] Welcome email send failed:', e)
      }

      // Переходим в App - профиль должен быть загружен
      console.log('[OnboardingPasswordNew] Navigating to /app...')
      window.location.href = '/app'
    } catch (err) {
      console.error('Sign up error:', err)
      setError(err?.message || 'Error creating account. Please try again.')
      setLoading(false)
    }
  }

  return (
    <div className="onboarding-page">
      <TopBar currentStep={10} totalSteps={10} onBack={onBack} showBack={true} />
      <div className="onboarding-content">
        <h1 className="h1">{t('onboardingPassword.createPassword')}</h1>
        <p className="p">
          {t('onboardingPassword.createPasswordSubtitle')}
        </p>

        <div className="field">
          <label className="label">{t('onboardingPassword.passwordLabel')}</label>
          <input
            className={`input ${error ? 'error' : ''}`}
            type="password"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value)
              if (error) setError('')
            }}
            placeholder="••••••••"
          />
        </div>

        <div className="field">
          <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', cursor: 'pointer' }}>
            <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} style={{ marginTop: 4 }} />
            <span className="p" style={{ marginTop: 0 }}>
              {t('onboardingPassword.agreeTermsBefore')}
              <button className="link" type="button" onClick={() => setLegal('terms-and-conditions')}>
                {t('onboardingPassword.termsOfUse')}
              </button>
              {t('onboardingPassword.agreeTermsBetween')}
              <button className="link" type="button" onClick={() => setLegal('privacy-policy')}>
                {t('onboardingPassword.privacyPolicy')}
              </button>
              {t('onboardingPassword.agreeTermsAfter')}
            </span>
          </label>
        </div>

        {error ? <div className="error-text">{error}</div> : null}
      </div>

      <button className="cta-fixed" onClick={onContinue} disabled={loading}>
        {loading ? t('onboardingPassword.creatingAccount') : t('common.continue')}
      </button>

      {legal ? (
        <Modal title={getLegalDocTitle(legal)} onClose={() => setLegal(null)}>
          {legal === 'terms-and-conditions' ? (
            <div style={{ maxHeight: '70vh', overflow: 'hidden', borderRadius: 8, border: '1px solid rgba(0,0,0,0.08)' }}>
              <iframe
                src={TERMS_PDF_URL}
                title={getLegalDocTitle(legal)}
                style={{ width: '100%', height: '70vh', border: 'none' }}
              />
              <p className="p" style={{ marginTop: 12 }}>
                If the document doesn&apos;t load,{' '}
                <a href={TERMS_PDF_URL} target="_blank" rel="noreferrer">
                  open it in a new tab
                </a>
                .
              </p>
            </div>
          ) : (
            <LegalDocContent docId={legal} />
          )}
        </Modal>
      ) : null}
    </div>
  )
}

export default OnboardingPasswordNew

